import { Panel, PanelHeaderTemplateOptions } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import NadaConsta from '../../../components/nadaConsta';
import { ListDescriptions } from '../../../components/ListDescriptions';
import { Score } from '../../../components/Score';
import { ScoreBar } from '../../../components/ScoreBar';
import { formatarParaMoedaBRL } from '../../../utils';

const LABELS: Record<string, string> = {
  consumerName: 'Nome',
  companyName: 'Razão Social',
  tradeName: 'Nome Fantasia',
  documentId: 'Documento',
  birthDate: 'Data de Nascimento',
  foundationDate: 'Data de Fundação',
  motherName: 'Nome da Mãe',
  statusRegistration: 'Situação do Documento',
  statusDate: 'Data da Situação',
  emissionDate: 'Data de Emissão',
  addressLine: 'Endereço',
  address: 'Endereço',
  district: 'Bairro',
  zipCode: 'CEP',
  city: 'Cidade',
  state: 'UF',
  country: 'País',
  number: 'Número',
  complement: 'Complemento',
  phone: 'Telefone',
  areaCode: 'DDD',
  phoneNumber: 'Telefone',
  email: 'E-mail',
  companyActivity: 'Atividade Econômica',
  legalNature: 'Natureza',
  natureDescription: 'Natureza',
  occurrenceDate: 'Data da Ocorrência',
  date: 'Data',
  amount: 'Valor',
  balance: 'Valor Total',
  count: 'Quantidade',
  creditorName: 'Credor',
  contractId: 'Contrato',
  principalCNPJ: 'CNPJ Principal',
  officeName: 'Cartório',
  officeNumber: 'Número do Cartório',
  checkNumber: 'Número do Cheque',
  bankName: 'Banco',
  bankAgencyId: 'Agência',
  bankId: 'Código do Banco',
  alinea: 'Alínea',
  score: 'Score',
  range: 'Faixa',
  defaultRate: 'Probabilidade de Inadimplência',
  message: 'Mensagem',
  partnerName: 'Nome do Sócio',
  participationPercentage: 'Participação (%)',
  updateDate: 'Data de Atualização',
  reportName: 'Relatório',
  distributorName: 'Distribuidor',
  processNumber: 'Número do Processo',
  civilCourt: 'Vara Cível',
  actionType: 'Tipo de Ação',
  author: 'Autor',
};

const SECOES_NEGATIVAS: Record<string, string> = {
  pefin: 'Pendências Financeiras (PEFIN)',
  refin: 'Restrições Financeiras (REFIN)',
  notary: 'Protestos',
  protests: 'Protestos',
  check: 'Cheques sem Fundos',
  checks: 'Cheques sem Fundos',
  collectionRecords: 'Registros de Cobrança',
  judgmentFilings: 'Ações Judiciais',
  lawsuits: 'Ações Judiciais',
  bankruptcies: 'Falências e Recuperações',
  bankruptcy: 'Falências e Recuperações',
  spcAnnotations: 'Anotações SPC',
};

const labelize = (key: string): string => {
  if (LABELS[key]) {
    return LABELS[key];
  }
  const spaced = key.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const isMoneyKey = (key: string): boolean => /amount|balance|valor/i.test(key);
const isDateKey = (key: string): boolean => /date|data/i.test(key);

const parseData = (value: any): Date | null => {
  if (!value) {
    return null;
  }
  const data = new Date(value);
  return isNaN(data.getTime()) ? null : data;
};

const formatValue = (key: string, value: any): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }
  if (typeof value === 'number' && isMoneyKey(key)) {
    return formatarParaMoedaBRL(value);
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [ano, mes, dia] = value.substring(0, 10).split('-');
    return `${dia}/${mes}/${ano}`;
  }
  return String(value);
};

const flatten = (obj: any, prefix?: string): { label: string, value: string }[] => {
  const dados: { label: string, value: string }[] = [];
  if (!obj) {
    return dados;
  }
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      dados.push(...flatten(value, labelize(key)));
    } else if (!Array.isArray(value)) {
      dados.push({ label: prefix && !LABELS[key] ? `${prefix} - ${labelize(key)}` : labelize(key), value: formatValue(key, value) });
    }
  });
  return dados;
};

const encontrarLista = (secao: any): any[] => {
  if (Array.isArray(secao)) {
    return secao;
  }
  const chaveLista = Object.keys(secao || {}).find((key) => Array.isArray(secao[key]));
  return chaveLista ? secao[chaveLista] : [];
};

const colunasDinamicas = (lista: any[]): string[] => {
  const chaves: string[] = [];
  lista.slice(0, 10).forEach((item) => {
    Object.keys(item || {}).forEach((key) => {
      if (!chaves.includes(key) && (item[key] === null || typeof item[key] !== 'object')) {
        chaves.push(key);
      }
    });
  });
  return chaves;
};

const tabelaDinamica = (lista: any[]) => (
  <DataTable size="small" value={lista} tableStyle={{ minWidth: '50rem' }}>
    {colunasDinamicas(lista).map((chave) => (
      <Column key={chave} body={(item: any) => formatValue(chave, item[chave])} header={labelize(chave)}></Column>
    ))}
  </DataTable>
);

const headerAlerta = (titulo: string, alerta: boolean) => (options: PanelHeaderTemplateOptions) => {
  const className = `${options.className} `;
  return (
    <div className={className}>
      {alerta ? (
        <span className="pi-consulta pi pi-exclamation-triangle" style={{ color: '#C70039' }}>
          <span className="p-panel-title" style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>{titulo}</span>
        </span>
      ) : (
        <span className="p-panel-title" style={{ marginLeft: '1rem', fontFamily: 'var(--font-family)', color: 'black' }}>{titulo}</span>
      )}
    </div>
  );
};

const resumoOcorrencias = (lista: any[]) => {
  const resumo: { icon: string, label: string, value: string }[] = [
    { icon: 'pi pi-chart-line', label: 'Quantidade Registros', value: String(lista.length) },
  ];
  const chaveValor = colunasDinamicas(lista).find((chave) => isMoneyKey(chave) && typeof lista[0][chave] === 'number');
  if (chaveValor) {
    const total = lista.reduce((soma, item) => soma + (Number(item[chaveValor]) || 0), 0);
    resumo.push({ icon: 'pi pi-dollar', label: 'Valor Total', value: formatarParaMoedaBRL(total) });
  }
  const chaveData = colunasDinamicas(lista).find((chave) => isDateKey(chave));
  if (chaveData) {
    const datas = lista.map((item) => parseData(item[chaveData])).filter((data): data is Date => data !== null);
    if (datas.length > 0) {
      const maisRecente = new Date(Math.max(...datas.map((data) => data.getTime())));
      resumo.push({ icon: 'pi pi-calendar-clock', label: 'Última Ocorrência', value: maisRecente.toLocaleDateString('pt-BR') });
    }
  }
  return resumo;
};

const ResumoCards = ({ itens }: { itens: { icon: string, label: string, value: string }[] }) => (
  <div className="flex-consulta flex-wrap gap-3" style={{ marginBottom: '1rem' }}>
    {itens.map((item, index) => (
      <Card className="flex-1" style={{ backgroundColor: '#2e2eb8' }} key={index}>
        <div className="flex-consulta justify-content-between gap-5">
          <div className="flex-consulta flex-column gap-1">
            <span style={{ color: 'white' }} className="text-sm">{item.label}</span>
            <span style={{ color: 'white' }} className="font-bold text-lg">{item.value}</span>
          </div>
          <span className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center" style={{ backgroundColor: 'white', color: '#183462' }}>
            <i className={item.icon} />
          </span>
        </div>
      </Card>
    ))}
  </div>
);

function SecaoNegativa({ chave, secao }: { chave: string, secao: any }) {
  const lista = encontrarLista(secao);
  const titulo = SECOES_NEGATIVAS[chave] || labelize(chave);
  return (
    <>
      <br></br>
      <Panel headerTemplate={headerAlerta(titulo, lista.length > 0)}>
        {lista.length === 0 ? (<NadaConsta></NadaConsta>) : (
          <>
            <ResumoCards itens={resumoOcorrencias(lista)} />
            {tabelaDinamica(lista)}
          </>
        )}
      </Panel>
    </>
  );
}

const rangeDoScore = (score: number): string => {
  if (score <= 200) return 'PESSIMO';
  if (score <= 400) return 'RUIM';
  if (score <= 600) return 'REGULAR';
  if (score <= 800) return 'BOM';
  return 'OTIMO';
};

function ScoreSerasa({ score }: { score: any }) {
  const valor = Number(score.score);
  const extras = flatten(score).filter((item) => item.label !== 'Score');
  return (
    <>
      <br></br>
      <Panel header="Score Serasa">
        {!isNaN(valor) && (
          <div style={{ display: 'flex', gap: '40px' }}>
            <div className="scrScore">
              <Score score={String(valor)} range={rangeDoScore(valor)} />
            </div>
            <ScoreBar score={String(valor)} rangeColor={String(valor)} />
          </div>
        )}
        {extras.length > 0 && (
          <ListDescriptions categoria="Detalhes do Score" dados={extras} />
        )}
      </Panel>
    </>
  );
}

function Relatorio({ report }: { report: any }) {
  const chavesTratadas = ['reportName', 'registration', 'score', 'negativeData', 'partnerships', 'partners', 'facts', 'optionalFeatures'];
  const score = report.score !== null && report.score !== undefined
    ? (typeof report.score === 'object' ? report.score : { score: report.score })
    : null;
  const partnerships = report.partnerships || report.partners;
  const listaSocios = partnerships ? encontrarLista(partnerships) : null;
  const outras = Object.keys(report).filter((key) => !chavesTratadas.includes(key) && report[key] !== null && typeof report[key] === 'object');

  return (
    <>
      {report.registration && (
        <>
          <br></br>
          <ListDescriptions categoria="Dados Cadastrais" dados={flatten(report.registration)} />
        </>
      )}
      {score && <ScoreSerasa score={score} />}
      {report.negativeData && Object.keys(report.negativeData).map((chave) => (
        report.negativeData[chave] !== null && typeof report.negativeData[chave] === 'object' && (
          <SecaoNegativa key={chave} chave={chave} secao={report.negativeData[chave]} />
        )
      ))}
      {listaSocios && (
        <>
          <br></br>
          <Panel header="Quadro Societário">
            {listaSocios.length === 0 ? (<NadaConsta></NadaConsta>) : tabelaDinamica(listaSocios)}
          </Panel>
        </>
      )}
      {report.facts && (
        <>
          <br></br>
          <ListDescriptions categoria="Informações Complementares" dados={flatten(report.facts)} />
        </>
      )}
      {outras.map((chave) => {
        const lista = encontrarLista(report[chave]);
        const ehLista = Array.isArray(report[chave]) || Object.keys(report[chave] || {}).some((k) => Array.isArray(report[chave][k]));
        return (
          <div key={chave}>
            <br></br>
            {ehLista ? (
              <Panel header={labelize(chave)}>
                {lista.length === 0 ? (<NadaConsta></NadaConsta>) : tabelaDinamica(lista)}
              </Panel>
            ) : (
              <ListDescriptions categoria={labelize(chave)} dados={flatten(report[chave])} />
            )}
          </div>
        );
      })}
    </>
  );
}

export default function SerasaRelatorio({
  relatorio,
  titulo
}: {
  relatorio: any,
  titulo: string
}) {
  if (!relatorio) {
    return null;
  }
  const reports: any[] = Array.isArray(relatorio.reports) && relatorio.reports.length > 0 ? relatorio.reports : [relatorio];

  return (
    <>
      <Panel header={titulo}>
        {relatorio.mensagemErro ? (<p>{relatorio.mensagemErro}</p>) : (
          reports.map((report, index) => (
            <Relatorio key={index} report={report}></Relatorio>
          ))
        )}
      </Panel>
    </>
  );
}
